import { useCallback, useEffect, useRef, useState } from 'react';

// Mide la altura visible (px) de un elemento vía ResizeObserver. Devuelve
// [refCallback, height] para pasarle un `height` numérico a rc-virtual-list.
// Fallback a window.innerHeight cuando no hay ResizeObserver o el nodo aún
// no reportó altura.
export function useElementHeight(): [(node: HTMLElement | null) => void, number] {
    const fallback = typeof window !== 'undefined' ? window.innerHeight : 0;
    const [height, setHeight] = useState<number>(fallback);
    const observerRef = useRef<ResizeObserver | null>(null);

    const refCallback = useCallback((node: HTMLElement | null): void => {
        if (observerRef.current) {
            observerRef.current.disconnect();
            observerRef.current = null;
        }
        if (!node) return;
        if (typeof ResizeObserver === 'undefined') {
            const local = typeof window !== 'undefined' ? window.innerHeight : 0;
            setHeight(node.clientHeight || local);
            return;
        }
        const observer = new ResizeObserver((entries) => {
            const measured = entries[0]?.contentRect.height ?? node.clientHeight;
            if (measured > 0) setHeight(measured);
        });
        observer.observe(node);
        if (node.clientHeight > 0) setHeight(node.clientHeight);
        observerRef.current = observer;
    }, []);

    useEffect(() => () => observerRef.current?.disconnect(), []);

    return [refCallback, height];
}
